import { Controller } from "@hotwired/stimulus"

const STORAGE_KEY = "wheel"

// The six-area wheel on the homepage.
//
// A reader taps a ring to say how far she has got with an area. The tap also
// opens that area's panel, so the wheel explains what the area is for, and
// "Where to start" below reorders itself to lead with the areas she has looked
// at least.
//
// Selection and marking are separate: which panel is open decides nothing but
// what the panel says. "Where to start" reads the marks and nothing else, and
// opening a panel to read it must never move the list underneath.
//
// Two things here are policy rather than implementation detail:
//
//   * Her marks never leave the browser. They live in this instance and in
//     sessionStorage, so the wheel can never read as a profile Lauza can see.
//     See PRIVACY_NOTE in Components::Home::Wheel, which has to stay true of
//     whatever this file does.
//   * The scale is what she has looked at, not how well she is doing. The marks
//     only ever decide what to show first; they never score her.
export default class extends Controller {
  static targets = [
    "band", "areaLabel", "panel", "rankRow", "progress", "summary", "blurb", "empty"
  ]

  static values = {
    selected: String,
    suggestions: Array,
    colors: Object
  }

  connect() {
    this.scores = {}
    this.#readState()
    this.#stripLegacyParams()
    this.paint()
  }

  // A ring: sets the mark and selects the area in the same gesture. Tapping the
  // ring that is already set clears it, so a mistake costs one tap.
  mark(event) {
    const { wheelArea: area, wheelLevel: level } = event.currentTarget.dataset
    const next = Number(level)

    this.scores[area] = this.scores[area] === next ? 0 : next
    this.selectedValue = area
    this.paint()
    this.#writeState()
  }

  select(event) {
    this.selectedValue = event.currentTarget.dataset.wheelArea
    this.paint()
    this.#writeState()
  }

  // ── Where the marks live ──────────────────────────────────────────────────
  //
  // sessionStorage, and deliberately not the query string.
  //
  // Marks used to die with the page, so a reload, or following a link and coming
  // back, wiped everything she had told the wheel. They were moved into the URL
  // to fix that, which worked and broke a promise doing it: the address bar goes
  // into her browser history, into this app's logs on the next request, into the
  // Referer header on any outbound link, and into the analytics script that reads
  // location.href on every pageview. PRIVACY_NOTE in Components::Home::Wheel says
  // nothing about her marks leaves the page, and that sentence is what holds a
  // self-marking control on the right side of the "nothing individualised" rule
  // in AGENTS.md. So the mechanism moved rather than the promise.
  //
  // sessionStorage keeps the reload and keeps the promise: it is same-origin,
  // never sent with a request, and gone when the tab closes.

  #readState() {
    const areas = this.#areaKeys()
    const top = this.suggestionsValue.length
    const stored = this.#storedState()

    Object.entries(stored.scores || {}).forEach(([ area, level ]) => {
      if (areas.includes(area) && Number(level) >= 1 && Number(level) <= top) {
        this.scores[area] = Number(level)
      }
    })

    if (areas.includes(stored.selected)) this.selectedValue = stored.selected
  }

  // Hand-editable, exactly like the query string it replaced, so it gets the same
  // treatment: unknown areas and out-of-range levels are dropped rather than
  // painted, and unreadable JSON is treated as no state at all.
  #storedState() {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {}
    } catch {
      return {}
    }
  }

  // Private browsing can refuse the write. Losing the reload is where this
  // started, and it is a better failure than the whole wheel throwing.
  #writeState() {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ scores: this.scores, selected: this.selectedValue })
      )
    } catch {
      // Nothing to do: the marks still work for this pageview.
    }
  }

  // A URL from the version that put marks in the address bar is still in
  // somebody's history or bookmarks, and `?track=` outlived the switch that read
  // it. Take them out of the address rather than read them back in, and leave a
  // bare URL bare.
  #stripLegacyParams() {
    const params = new URLSearchParams(window.location.search)
    if (!params.has("marks") && !params.has("area") && !params.has("track")) return

    params.delete("marks")
    params.delete("area")
    params.delete("track")
    const query = params.toString()
    history.replaceState(null, "", query ? `${window.location.pathname}?${query}` : window.location.pathname)
  }

  #areaKeys() {
    return this.panelTargets.map((el) => el.dataset.wheelArea)
  }

  paint() {
    this.#paintRings()
    this.#paintLabels()
    this.#paintPanels()
    this.#paintStart()
  }

  #paintRings() {
    const { ink, tan, soft } = this.colorsValue

    this.bandTargets.forEach((el) => {
      const area = el.dataset.wheelArea
      const on = (this.scores[area] || 0) >= Number(el.dataset.wheelLevel)

      el.setAttribute("fill", on ? (area === this.selectedValue ? ink : tan) : soft)
    })
  }

  // The open area's name stays underlined, so the label is not only something
  // that reacts to a hover but something that shows which one is being read.
  #paintLabels() {
    this.areaLabelTargets.forEach((el) => {
      const text = el.querySelector("text")
      if (!text) return

      text.setAttribute(
        "text-decoration",
        el.dataset.wheelArea === this.selectedValue ? "underline" : "none"
      )
    })
  }

  #paintPanels() {
    this.panelTargets.forEach((el) => {
      el.hidden = el.dataset.wheelArea !== this.selectedValue
    })
  }

  // ── Where to start ────────────────────────────────────────────────────────
  //
  // One input, and the open panel is not it: the areas she marked in step one.
  // Lowest mark first, so what she has looked at least comes first. An area she
  // says she has covered still lists its pieces — it sorts last and carries a
  // note saying where the rest are, rather than counting pieces at her without
  // showing any.

  #paintStart() {
    let marked = 0
    let suggested = 0

    this.rankRowTargets.forEach((row) => {
      const score = this.scores[row.dataset.wheelArea] || 0
      const limit = this.suggestionsValue[score - 1]

      row.hidden = score === 0
      row.style.order = score
      if (score) marked++

      row.querySelectorAll("[data-wheel-starter]").forEach((piece) => {
        const on = score > 0 && Number(piece.dataset.wheelStarter) <= limit
        piece.hidden = !on
        if (on) suggested++
      })

      const covered = row.querySelector("[data-wheel-covered]")
      if (covered) covered.hidden = score !== this.suggestionsValue.length
    })

    // Before she marks anything, step three is one sentence and nothing else.
    // The blurb describes how the list is ordered and where the rest of the
    // writing went, which is only worth saying once there is a list.
    if (this.hasEmptyTarget) this.emptyTarget.hidden = marked > 0

    if (this.hasBlurbTarget) this.blurbTarget.hidden = marked === 0

    // Nothing until she has marked something: the count is of her own marks, so
    // before there are any there is nothing to say.
    if (this.hasSummaryTarget) {
      this.summaryTarget.textContent = marked === 0
        ? ""
        : `${this.#plural(marked, "area")} marked · ${this.#plural(suggested, "piece")} to start with`
    }

    if (this.hasProgressTarget) {
      this.progressTarget.textContent = `${marked} OF ${this.#areaKeys().length} MARKED`
    }
  }

  #plural(n, word) {
    return `${n} ${word}${n === 1 ? "" : "s"}`
  }
}
